// Vendor imports
const React = require('react')

function registerEventHandlerShim(componentInstance, key) {
  componentInstance[key] = event => {
    const { props } = componentInstance
    if (typeof props[key] === 'function') {
      let options = props
      if (event) {
        event.preventDefault()
        event.stopPropagation()
        options = { ...props, event }
      }
      props[key](options)
    }
  }
}

function registerComponentEventHandlers(componentInstance) {
  const { constructor } = componentInstance
  const { eventHandlerKeys } = constructor
  if (!eventHandlerKeys) {
    throw new Error(`Component[${constructor.name}] is missing eventHandlerKeys`)
  }
  eventHandlerKeys.forEach(key => {
    registerEventHandlerShim(componentInstance, key)
  })
}

/**
 * This should not be necessary; a comprehensive version of this is built-into React.
 * Unfortunately, `shared/getComponentName.js` is not exposed in the public React API.
 * @see https://github.com/facebook/react/blob/master/packages/shared/getComponentName.js
 * @see https://github.com/facebook/react/issues/14319
 */
function getDisplayName(component) {
  if (component == null) {
    return null
  }
  return (
    component.displayName || component.name ||
    (typeof component === 'string' ? component : 'Component')
  )
}

function wrapDisplayName(wrapper, wrapped) {
  const wrapperName = getDisplayName(wrapper)
  const wrappedName = getDisplayName(wrapped)
  return `${wrapperName}(${wrappedName})`
}

/**
 * `domRef` needs to be attached to a DOM node;
 * if `child` is not a DOM element, forward it down.
 * Child React components **must** handle `forwardedRef`.
 * @param {React.ElementType} child
 * @param {React.Ref<any>} domRef
 * @returns {React.ElementType}
 */
function cloneElementWithDomRef(child, domRef) {
  let refKey
  let existingRef
  if (typeof child.type === 'string') {
    refKey = 'ref'
    existingRef = child.ref
  } else {
    refKey = 'forwardedRef'
    existingRef = child.props[refKey]
  }
  return React.cloneElement(child, {
    [refKey]: existingRef ? composeRefs(existingRef, domRef) : domRef
  })
}

function composeRefs(...refs) {
  // micro-optimize the hot path
  if (refs.length === 2) {
    return composeTwoRefs(refs[0], refs[1])
  }
  return refs.slice(1).reduce(composeTwoRefs, refs[0])
}

function composeTwoRefs(refA, refB) {
  if (refA && refB) {
    return instance => {
      updateRef(refA, instance)
      updateRef(refB, instance)
    }
  }
  return refA || refB
}

function updateRef(ref, instance) {
  if (typeof ref === 'function') {
    ref(instance)
  } else {
    ref.current = instance
  }
}

module.exports = {
  // Event handler utils
  registerEventHandlerShim,
  registerComponentEventHandlers,
  // Display name utils
  getDisplayName,
  wrapDisplayName,
  // Ref utils
  cloneElementWithDomRef,
  composeRefs,
  updateRef
}
